import {
  IGovernancePromptMonitoringStatsCombinedFilters,
  IGovernancePromptMonitoringStatsFilters,
  IGovernancePromptMonitoringStatsService,
  OpenApi,
} from '@edgebox/sync-core-rest-client';
import { ICombinableStatsRequest, statsRequestKey } from './statsRequestQueue';

/**
 * The AI Visibility statistics requests as the statistics queue sends them: each by its own route,
 * or as one aspect of a combined request with the others waiting over the same answers.
 *
 * A caller spreads what a builder returns into `scheduleStatsRequest`, beside its priority and
 * signal; the queue decides whether it travels alone or combined, and the caller receives the same
 * answer either way.
 */

type AspectKind = OpenApi.GovernancePromptMonitoringStatsAspectKind;
type AspectRequest = OpenApi.GovernancePromptMonitoringStatsAspectRequest;
type CombinedItem = OpenApi.GovernancePromptMonitoringStatsCombinedItemResponse;
type StatsService = IGovernancePromptMonitoringStatsService;

/** What `scheduleStatsRequest` needs of a combinable request, the priority and signal aside. */
export interface IPromptMonitoringStatsRequest<T> {
  key: string;
  run: () => Promise<T>;
  combinable: ICombinableStatsRequest;
}

/** The client surface a request calls: the route it stands for and the combined route. */
type StatsApi<Method extends keyof StatsService> = {
  governance: { promptMonitoringStats: Pick<StatsService, Method | 'combined'> };
};

/**
 * The filters that select the answers, and the time zone they are counted in: the same for every
 * aspect of a combined request, so they are what makes requests combinable.
 */
const SHARED_FILTER_KEYS = [
  'purposeTermKey',
  'promptKey',
  'engineKey',
  'localeKey',
  'country',
  'taxonomyTerms',
  'timezone',
  'from',
  'to',
] as const satisfies readonly (keyof IGovernancePromptMonitoringStatsFilters)[];

/**
 * The filters no aspect of a combined request reads. Sync Core reads `buckets` for a history alone,
 * and a history in a combined request takes its own count instead (`numberOfDays`, `numberOfWeeks`,
 * `numberOfMonths`). They stay out of the combined request, so they never keep apart requests that
 * could travel together, and a request sent by its own route still carries them.
 */
const UNREAD_FILTER_KEYS = ['buckets'] as const satisfies readonly (keyof IGovernancePromptMonitoringStatsFilters)[];

/** The filters that shape one aspect's answer; each aspect carries its own. */
const ASPECT_FILTER_KEYS = [
  'facetTaxonomyKey',
  'includeSources',
  'entityTermKey',
  'groupBy',
  'sourceHosts',
  'xAxisTaxonomyKey',
  'yAxisTaxonomyKey',
  'xAxisTerms',
  'yAxisTerms',
] as const satisfies readonly (keyof IGovernancePromptMonitoringStatsFilters & keyof AspectRequest)[];

/**
 * Every filter the routes take is in one of the three lists: a filter added to the client and to
 * none of them fails this build rather than going missing from combined requests.
 */
type UnplacedFilter = Exclude<
  keyof IGovernancePromptMonitoringStatsFilters,
  (typeof SHARED_FILTER_KEYS)[number] | (typeof ASPECT_FILTER_KEYS)[number] | (typeof UNREAD_FILTER_KEYS)[number]
>;
/** Compiles only while no filter is unplaced; an unplaced one is named in the error. */
type EveryFilterIsPlaced<Unplaced extends never> = Unplaced;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type FilterPlacement = EveryFilterIsPlaced<UnplacedFilter>;

/** Where each kind's answer sits in its item of the combined response. */
const ANSWER_OF_KIND: Record<AspectKind, (item: CombinedItem) => unknown> = {
  [OpenApi.GovernancePromptMonitoringStatsAspectKind.Total]: (item) => item.total,
  [OpenApi.GovernancePromptMonitoringStatsAspectKind.DailyHistory]: (item) => item.dailyHistory,
  [OpenApi.GovernancePromptMonitoringStatsAspectKind.WeeklyHistory]: (item) => item.weeklyHistory,
  [OpenApi.GovernancePromptMonitoringStatsAspectKind.MonthlyHistory]: (item) => item.monthlyHistory,
  [OpenApi.GovernancePromptMonitoringStatsAspectKind.Matrix]: (item) => item.matrix,
  [OpenApi.GovernancePromptMonitoringStatsAspectKind.EntityDomains]: (item) => item.entityDomains,
};

/**
 * One statistics request as an aspect of a combined request: the filters it shares with the others,
 * its own filters and counts, and where its answer sits in its item.
 */
export function promptMonitoringAspect(
  api: StatsApi<never>,
  {
    kind,
    filters,
    counts = {},
  }: {
    kind: AspectKind;
    filters: IGovernancePromptMonitoringStatsCombinedFilters;
    counts?: Pick<AspectRequest, 'numberOfDays' | 'numberOfWeeks' | 'numberOfMonths'>;
  }
): ICombinableStatsRequest {
  const sharedFilters: IGovernancePromptMonitoringStatsCombinedFilters = {};
  for (const key of SHARED_FILTER_KEYS) {
    if (filters[key] !== undefined) {
      Object.assign(sharedFilters, { [key]: filters[key] });
    }
  }
  const aspect: Omit<AspectRequest, 'id'> = { kind, ...counts };
  for (const key of ASPECT_FILTER_KEYS) {
    if (filters[key] !== undefined) {
      Object.assign(aspect, { [key]: filters[key] });
    }
  }

  return {
    sharedFilters,
    aspect,
    answerOf: (item) => {
      const answer = ANSWER_OF_KIND[kind](item);
      // An item without its answer carries nothing this caller can use.
      if (answer === undefined || answer === null) {
        throw new Error(`The combined statistics response carried no ${kind} answer for this request.`);
      }
      return answer;
    },
    sendCombined: (combinedFilters, aspects) => api.governance.promptMonitoringStats.combined(combinedFilters, aspects),
  };
}

/** The totals over the answers the filters select: `total`. */
export function promptMonitoringTotalRequest(
  api: StatsApi<'total'>,
  filters: IGovernancePromptMonitoringStatsFilters
): IPromptMonitoringStatsRequest<OpenApi.GovernancePromptMonitoringStatsTotalResponse> {
  return {
    key: statsRequestKey({ method: 'total', parameters: [filters] }),
    run: () => api.governance.promptMonitoringStats.total(filters),
    combinable: promptMonitoringAspect(api, { kind: OpenApi.GovernancePromptMonitoringStatsAspectKind.Total, filters }),
  };
}

/** The history by day, the number of days given: `dailyHistory`. */
export function promptMonitoringDailyHistoryRequest(
  api: StatsApi<'dailyHistory'>,
  { days, filters }: { days: number; filters: IGovernancePromptMonitoringStatsFilters }
): IPromptMonitoringStatsRequest<OpenApi.GovernancePromptMonitoringStatsDailyHistoryBucketResponse[]> {
  return {
    key: statsRequestKey({ method: 'dailyHistory', parameters: [days, filters] }),
    run: () => api.governance.promptMonitoringStats.dailyHistory(days, filters),
    combinable: promptMonitoringAspect(api, {
      kind: OpenApi.GovernancePromptMonitoringStatsAspectKind.DailyHistory,
      filters,
      counts: { numberOfDays: days },
    }),
  };
}

/** The history by week, the number of weeks given: `weeklyHistory`. */
export function promptMonitoringWeeklyHistoryRequest(
  api: StatsApi<'weeklyHistory'>,
  { weeks, filters }: { weeks: number; filters: IGovernancePromptMonitoringStatsFilters }
): IPromptMonitoringStatsRequest<OpenApi.GovernancePromptMonitoringStatsWeeklyHistoryBucketResponse[]> {
  return {
    key: statsRequestKey({ method: 'weeklyHistory', parameters: [weeks, filters] }),
    run: () => api.governance.promptMonitoringStats.weeklyHistory(weeks, filters),
    combinable: promptMonitoringAspect(api, {
      kind: OpenApi.GovernancePromptMonitoringStatsAspectKind.WeeklyHistory,
      filters,
      counts: { numberOfWeeks: weeks },
    }),
  };
}

/** The history by month, the number of months given: `monthlyHistory`. */
export function promptMonitoringMonthlyHistoryRequest(
  api: StatsApi<'monthlyHistory'>,
  { months, filters }: { months: number; filters: IGovernancePromptMonitoringStatsFilters }
): IPromptMonitoringStatsRequest<OpenApi.GovernancePromptMonitoringStatsMonthlyHistoryBucketResponse[]> {
  return {
    key: statsRequestKey({ method: 'monthlyHistory', parameters: [months, filters] }),
    run: () => api.governance.promptMonitoringStats.monthlyHistory(months, filters),
    combinable: promptMonitoringAspect(api, {
      kind: OpenApi.GovernancePromptMonitoringStatsAspectKind.MonthlyHistory,
      filters,
      counts: { numberOfMonths: months },
    }),
  };
}

/** The domain each tracked entity's answers point to: `entityDomains`. */
export function promptMonitoringEntityDomainsRequest(
  api: StatsApi<'entityDomains'>,
  filters: IGovernancePromptMonitoringStatsFilters
): IPromptMonitoringStatsRequest<Awaited<ReturnType<StatsService['entityDomains']>>> {
  return {
    key: statsRequestKey({ method: 'entityDomains', parameters: [filters] }),
    run: () => api.governance.promptMonitoringStats.entityDomains(filters),
    combinable: promptMonitoringAspect(api, { kind: OpenApi.GovernancePromptMonitoringStatsAspectKind.EntityDomains, filters }),
  };
}

/** The scores of one taxonomy's terms against another's: `matrix`. */
export function promptMonitoringMatrixRequest(
  api: StatsApi<'matrix'>,
  filters: IGovernancePromptMonitoringStatsFilters
): IPromptMonitoringStatsRequest<OpenApi.GovernancePromptMonitoringStatsMatrixResponse> {
  return {
    key: statsRequestKey({ method: 'matrix', parameters: [filters] }),
    run: () => api.governance.promptMonitoringStats.matrix(filters),
    combinable: promptMonitoringAspect(api, { kind: OpenApi.GovernancePromptMonitoringStatsAspectKind.Matrix, filters }),
  };
}
